import React, { useState, useEffect } from 'react';
import {
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  Snackbar,
  Autocomplete,
  Chip,
  Collapse,
  InputAdornment
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Category as CategoryIcon,
  Visibility as VisibilityIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  SelectAll as SelectAllIcon,
  Clear as ClearIcon
} from '@mui/icons-material';

const LessonCategoryManagement = () => {
  const [lessonCategories, setLessonCategories] = useState([]);
  const [lessonTypes, setLessonTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState({
    name: '',
    lesson_type_ids: []
  });
  const [bulkSelectionKeyword, setBulkSelectionKeyword] = useState('');
  const [showBulkTools, setShowBulkTools] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      console.log('Fetching lesson categories and lesson types...');
      const [categoriesRes, typesRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/lesson-category/`, {
          headers: { 'Access-Token': process.env.REACT_APP_API_TOKEN }
        }),
        fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/lesson-type/`, {
          headers: { 'Access-Token': process.env.REACT_APP_API_TOKEN }
        })
      ]);

      if (categoriesRes.ok) {
        const data = await categoriesRes.json();
        console.log('Lesson categories data:', data);
        setLessonCategories(data.objects || []);
      } else {
        console.error('Failed to fetch lesson categories:', await categoriesRes.text());
        showSnackbar('Nie udało się pobrać kategorii zajęć', 'error');
      }

      if (typesRes.ok) {
        const data = await typesRes.json();
        console.log('Lesson types data:', data);
        setLessonTypes(data.objects || []);
      } else {
        console.error('Failed to fetch lesson types:', await typesRes.text());
        showSnackbar('Nie udało się pobrać typów zajęć', 'error');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      showSnackbar('Błąd podczas ładowania danych', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const method = editingCategory ? 'PUT' : 'POST';
      const url = editingCategory
        ? `${process.env.REACT_APP_BACKEND_ADDRESS}/lesson-category/${editingCategory.id}`
        : `${process.env.REACT_APP_BACKEND_ADDRESS}/lesson-category/`;

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Access-Token': process.env.REACT_APP_API_TOKEN
        },
        body: JSON.stringify({
          name: formData.name,
          lesson_type_ids: formData.lesson_type_ids
        })
      });

      if (response.ok) {
        await fetchData();
        setOpen(false);
        resetForm();
        setEditingCategory(null);
        showSnackbar(
          editingCategory
            ? 'Kategoria zajęć została pomyślnie zaktualizowana!'
            : 'Kategoria zajęć została pomyślnie utworzona!',
          'success'
        );
      } else {
        const error = await response.text();
        showSnackbar(
          `Błąd podczas ${editingCategory ? 'aktualizacji' : 'tworzenia'} kategorii zajęć: ${error}`,
          'error'
        );
      }
    } catch (error) {
      showSnackbar(`Błąd: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      lesson_type_ids: []
    });
    setEditingCategory(null);
    setBulkSelectionKeyword('');
    setShowBulkTools(false);
  };

  const handleEdit = (category) => {
    setFormData({
      name: category.name,
      lesson_type_ids: category.lesson_types ? category.lesson_types.map(lt => lt.id) : []
    });
    setEditingCategory(category);
    setOpen(true);
  };

  const handleDelete = async (category) => {
    if (window.confirm(`Czy na pewno chcesz usunąć kategorię "${category.name}"?`)) {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/lesson-category/${category.id}`, {
          method: 'DELETE',
          headers: {
            'Access-Token': process.env.REACT_APP_API_TOKEN
          }
        });

        if (response.ok) {
          await fetchData();
          showSnackbar('Kategoria zajęć została pomyślnie usunięta!', 'success');
        } else {
          const error = await response.text();
          showSnackbar(`Błąd podczas usuwania kategorii zajęć: ${error}`, 'error');
        }
      } catch (error) {
        showSnackbar(`Błąd: ${error.message}`, 'error');
      }
    }
  };

  const handleOpenCreate = () => {
    resetForm();
    setOpen(true);
  };

  const handleViewDetails = (category) => {
    setSelectedCategory(category);
    setDetailsOpen(true);
  };

  // Bulk selection functions
  const handleBulkSelectByKeyword = (keyword) => {
    if (!keyword.trim()) return;

    const matchingTypes = lessonTypes.filter(type =>
      type.name.toLowerCase().includes(keyword.toLowerCase()) ||
      (type.friendly_name && type.friendly_name.toLowerCase().includes(keyword.toLowerCase()))
    );

    const matchingIds = matchingTypes.map(type => type.id);
    const currentIds = formData.lesson_type_ids;
    const newIds = [...new Set([...currentIds, ...matchingIds])]; // Remove duplicates

    handleInputChange('lesson_type_ids', newIds);
    showSnackbar(`Dodano ${matchingTypes.length} typów zajęć zawierających "${keyword}"`, 'success');
  };

  const handleSelectAllLessonTypes = () => {
    const allIds = lessonTypes.map(type => type.id);
    handleInputChange('lesson_type_ids', allIds);
    showSnackbar(`Wybrano wszystkie ${lessonTypes.length} typów zajęć`, 'success');
  };

  const handleClearAllLessonTypes = () => {
    handleInputChange('lesson_type_ids', []);
    showSnackbar('Wyczyszczono wybrane typy zajęć', 'info');
  };

  const getMatchingTypesCount = (keyword) => {
    if (!keyword.trim()) return 0;
    return lessonTypes.filter(type =>
      type.name.toLowerCase().includes(keyword.toLowerCase()) ||
      (type.friendly_name && type.friendly_name.toLowerCase().includes(keyword.toLowerCase()))
    ).length;
  };

  return (
    <>
      <Box>
        {/* Header */}
        <Box
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          mb={3}
          gap={2}
        >
          <Box>
            <Typography
              variant="h5"
              component="h2"
              gutterBottom
              sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}
            >
              Zarządzanie Kategoriami Zajęć
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Twórz, edytuj i zarządzaj kategoriami zajęć dostępnych w studiu
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenCreate}
            size="large"
            startIcon={<AddIcon />}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontSize: { xs: '0.875rem', sm: '1rem' },
              px: { xs: 2, sm: 3 },
              whiteSpace: 'nowrap',
              minWidth: { sm: 'fit-content' },
              width: { xs: '100%', sm: 'auto' }
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              Dodaj Nową Kategorię
            </Box>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
              Dodaj Kategorię
            </Box>
          </Button>
        </Box>

        {lessonCategories.length > 0 ? (
          <Box sx={{ overflowX: 'auto' }}>
            <TableContainer component={Paper} elevation={2} sx={{ minWidth: { xs: 300, sm: 'auto' } }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, fontWeight: 600 }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <CategoryIcon fontSize="small" />
                        Nazwa Kategorii
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, fontWeight: 600, display: { xs: 'none', md: 'table-cell' } }}>
                      Przypisane Typy Zajęć
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, fontWeight: 600 }}>
                      Akcje
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lessonCategories.map(category => (
                    <TableRow
                      key={category.id}
                      hover
                      sx={{
                        '&:hover': {
                          backgroundColor: '#f8f9fa',
                        }
                      }}
                    >
                      <TableCell sx={{ padding: { xs: '8px 16px', sm: '16px' } }}>
                        <Box>
                          <Typography
                            variant="body1"
                            fontWeight={500}
                            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                          >
                            {category.name}
                          </Typography>
                          {/* Show lesson types count on mobile */}
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: { xs: 'block', md: 'none' },
                              fontSize: '0.7rem'
                            }}
                          >
                            Typy: {category.lesson_types ? category.lesson_types.length : 0}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, padding: { xs: '8px 4px', sm: '16px' } }}>
                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                          {category.lesson_types && category.lesson_types.length > 0 ? (
                            category.lesson_types.slice(0, 3).map((lessonType) => (
                              <Chip
                                key={lessonType.id}
                                label={lessonType.name}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.75rem' }}
                              />
                            ))
                          ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              Brak przypisanych typów
                            </Typography>
                          )}
                          {category.lesson_types && category.lesson_types.length > 3 && (
                            <Chip
                              label={`+${category.lesson_types.length - 3}`}
                              size="small"
                              variant="filled"
                              color="primary"
                              sx={{ fontSize: '0.75rem' }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={{ padding: { xs: '8px 4px', sm: '16px' } }}>
                        <Box display="flex" gap={0.5} justifyContent="center">
                          <IconButton
                            color="info"
                            size="small"
                            onClick={() => handleViewDetails(category)}
                            title="Pokaż szczegóły"
                            sx={{
                              '&:hover': {
                                backgroundColor: 'info.light',
                                color: 'white'
                              }
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={() => handleEdit(category)}
                            title="Edytuj"
                            sx={{
                              '&:hover': {
                                backgroundColor: 'primary.light',
                                color: 'white'
                              }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => handleDelete(category)}
                            title="Usuń"
                            sx={{
                              '&:hover': {
                                backgroundColor: 'error.light',
                                color: 'white'
                              }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <CategoryIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Brak kategorii zajęć
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Nie znaleziono żadnych kategorii zajęć. Dodaj pierwszą kategorię, aby zacząć.
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleOpenCreate}
              startIcon={<AddIcon />}
            >
              Dodaj Pierwszą Kategorię
            </Button>
          </Paper>
        )}
      </Box>

      {/* Create/Edit Dialog */}
      <Dialog open={open} onClose={() => { setOpen(false); resetForm(); }} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCategory ? 'Edytuj Kategorię Zajęć' : 'Utwórz Nową Kategorię Zajęć'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              label="Nazwa Kategorii"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              required
              margin="normal"
              helperText="Wprowadź nazwę kategorii zajęć (np. 'Yoga', 'Pilates', 'Fitness')"
              autoFocus
            />

            {/* Bulk Selection Tools */}
            <Box sx={{ mt: 2, mb: 1 }}>
              <Button
                onClick={() => setShowBulkTools(!showBulkTools)}
                size="small"
                startIcon={<ExpandMoreIcon sx={{
                  transform: showBulkTools ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s'
                }} />}
                sx={{ textTransform: 'none', color: 'text.secondary' }}
              >
                Narzędzia masowego wyboru
              </Button>
            </Box>

            <Collapse in={showBulkTools}>
              <Box sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f8f9fa' }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                  Szybki wybór typów zajęć
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <TextField
                    size="small"
                    label="Słowo kluczowe"
                    value={bulkSelectionKeyword}
                    onChange={(e) => setBulkSelectionKeyword(e.target.value)}
                    placeholder="np. yoga, pilates, fitness"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ minWidth: 200 }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleBulkSelectByKeyword(bulkSelectionKeyword);
                      }
                    }}
                  />

                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleBulkSelectByKeyword(bulkSelectionKeyword)}
                    disabled={!bulkSelectionKeyword.trim()}
                    startIcon={<AddIcon />}
                  >
                    Dodaj pasujące
                  </Button>
                </Box>

                {bulkSelectionKeyword.trim() && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Znaleziono {getMatchingTypesCount(bulkSelectionKeyword)} typów zawierających "{bulkSelectionKeyword}"
                  </Typography>
                )}

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleSelectAllLessonTypes}
                    startIcon={<SelectAllIcon />}
                    color="info"
                  >
                    Wybierz wszystkie ({lessonTypes.length})
                  </Button>

                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleClearAllLessonTypes}
                    startIcon={<ClearIcon />}
                    color="secondary"
                    disabled={formData.lesson_type_ids.length === 0}
                  >
                    Wyczyść wybór
                  </Button>
                </Box>
              </Box>
            </Collapse>

            <Autocomplete
              multiple
              options={lessonTypes}
              getOptionLabel={(option) => `${option.name} (${option.price} PLN)`}
              value={lessonTypes.filter(type => formData.lesson_type_ids.includes(type.id))}
              onChange={(event, newValue) => {
                handleInputChange('lesson_type_ids', newValue.map(type => type.id));
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={`${option.name} (${option.price} PLN)`}
                    {...getTagProps({ index })}
                    key={option.id}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Przypisane Typy Zajęć"
                  placeholder="Wybierz typy zajęć..."
                  margin="normal"
                  helperText="Wybierz typy zajęć, które będą należeć do tej kategorii. Możesz wybrać wiele typów."
                />
              )}
              sx={{ mt: 2 }}
            />
          </DialogContent>

          <DialogActions>
            <Button onClick={() => { setOpen(false); resetForm(); }}>
              Anuluj
            </Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading
                ? (editingCategory ? 'Aktualizowanie...' : 'Tworzenie...')
                : (editingCategory ? 'Aktualizuj' : 'Utwórz')
              }
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Szczegóły Kategorii Zajęć</DialogTitle>
        <DialogContent>
          {selectedCategory && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                {selectedCategory.name}
              </Typography>

              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                Przypisane Typy Zajęć:
              </Typography>

              {selectedCategory.lesson_types && selectedCategory.lesson_types.length > 0 ? (
                <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                  {selectedCategory.lesson_types.map((lessonType) => (
                    <Chip
                      key={lessonType.id}
                      label={`${lessonType.name} (${lessonType.price} PLN)`}
                      variant="filled"
                      color="primary"
                      sx={{
                        backgroundColor: lessonType.bg_color || 'primary.main',
                        color: 'white',
                        '& .MuiChip-label': {
                          fontWeight: 500
                        }
                      }}
                    />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 2 }}>
                  Brak przypisanych typów zajęć do tej kategorii.
                </Typography>
              )}

              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Łącznie typów zajęć: <strong>{selectedCategory.lesson_types ? selectedCategory.lesson_types.length : 0}</strong>
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Zamknij</Button>
          <Button
            onClick={() => {
              setDetailsOpen(false);
              handleEdit(selectedCategory);
            }}
            variant="contained"
            color="primary"
          >
            Edytuj
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LessonCategoryManagement;